/**
 * Edge-to-edge drawing on iOS 26 Safari (Liquid Glass toolbars).
 *
 * Safari clips every `position: fixed` / `sticky` layer to the strip
 * between its status bar and floating bottom bar, and fills the bars with
 * a colour sampled from the first such layer it sees. That colour then
 * sticks (restyling or covering the layer doesn't refresh it), which is
 * how the page-transition curtain used to tint the bars long after it
 * was gone. Content that isn't fixed draws under both bars.
 *
 * So the window never scrolls and no full-screen layer is fixed:
 *  - `html` is overflow: hidden. `#root` is `--bleed` taller than the
 *    viewport at the top and at the bottom, and the window stays pinned
 *    scrolled down by `--bleed`, which puts #root's top bleed under the
 *    status bar and its bottom bleed under the bottom bar.
 *  - `.stage` is the visible area (what `fixed inset-0` used to be) for
 *    UI; `.stage-bleed` is all of #root, for backgrounds and canvases.
 *  - Nothing else is fixed either, UI included (Nav, Footer, loader):
 *    in landscape Safari also samples fixed layers touching the sides to
 *    fill the safe areas beside the Dynamic Island. With the window
 *    pinned, `absolute` against the visible area looks the same.
 * With nothing fixed to sample, Safari shows the root background behind
 * the bars and follows it live, so on iOS the root colour is kept matching
 * whatever is at the screen edges (`tracksScreenEdges`), and the body is
 * transparent so the root shows. Desktop Safari tints its toolbar from the
 * body background instead, which stays dark everywhere else, and the root
 * only changes once per page there, as it always did. `--bleed` is 0
 * except on iOS, where nothing else changes.
 */

const isIOS =
    typeof navigator !== "undefined" &&
    (/iP(hone|od|ad)/.test(navigator.userAgent) ||
        // iPadOS reports a Mac user agent.
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

/** iOS: the page draws under the bars, which blend into the root colour,
 *  so the root follows what's on screen (curtain, Index room, case-study
 *  panel) and the body is transparent. Elsewhere the body keeps its dark
 *  background (desktop Safari's toolbar colour) and the root changes only
 *  per page, as before. */
export const tracksScreenEdges = isIOS;

let bleed = 0;

/** Current bleed (px) above and below the visible area. */
export const getBleed = () => bleed;

/** Screen height minus the visible area: everything the browser's bars
 *  cover, so it overshoots each edge (the overshoot is never on screen). */
function measureBleed(): number {
    if (!isIOS) return 0;
    const landscape = window.innerWidth > window.innerHeight;
    const screenHeight = landscape
        ? Math.min(screen.width, screen.height)
        : Math.max(screen.width, screen.height);
    return Math.max(0, Math.round(screenHeight - window.innerHeight));
}

/** Keeps the window scrolled by exactly the bleed. Nothing scrolls it on
 *  purpose; this undoes anything that does (focus, anchor, keyboard). */
function pin() {
    if (window.scrollY !== bleed || window.scrollX !== 0) {
        window.scrollTo({ top: bleed, left: 0, behavior: "instant" });
    }
}

function apply() {
    bleed = measureBleed();
    document.documentElement.style.setProperty("--bleed", `${bleed}px`);
    pin();
}

/** Call once before the first render. */
export function initEdgeToEdge() {
    // iOS: transparent body, so the root colour is what Safari sees.
    if (tracksScreenEdges) document.documentElement.classList.add("edge-to-edge");
    apply();
    window.addEventListener("resize", apply);
    window.addEventListener("scroll", pin, { passive: true });
}

/** The colour iOS Safari shows behind (and blends into at) its bars. */
export function setRootBackground(color: string) {
    document.documentElement.style.backgroundColor = color;
}

/** Until the app has painted, only the root shows, in the colour
 *  index.html's head script chose for the address (the app's own first
 *  frame, see PageTransition/routeThemes.ts), and the body is transparent
 *  so its dark cannot flash under a light page. Called by PageTransition
 *  once its first frame covers the page; the body is dark from then on
 *  (src/styles/index.css). */
export function markAppPainted() {
    document.documentElement.classList.add("app-painted");
}

